import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/useAuth";
import { SunwaveLogo } from "../components/common/SunwaveLogo";

/**
 * Splash / sign-in landing.
 *
 * Renders one of four shapes based on the auth state machine:
 *
 *   - **Default (signed out)** — hex.tech-inspired hero + three-pillar
 *     marketing layout with a "Start" CTA that triggers
 *     ``signInWithRedirect``.
 *   - **Provisioning** — a compact card with "Setting up your account…"
 *     plus an always-visible "Sign out" escape so the user can never be
 *     trapped.
 *   - **Provisioning timed out** (>15s) — compact card with a hard
 *     error message and "Sign out" button.
 *   - **Authenticated** — redirect to /projects via the effect below.
 *
 * Raw axios error strings never reach this component. All auth failures
 * are classified inside AuthProvider into one of the states above.
 *
 * The marketing layout uses inline styles (no CSS module needed; the
 * font stack and base color tokens are inherited from index.css).
 */
export function HomePage() {
  const {
    isAuthenticated,
    isLoading,
    signIn,
    signOut,
    provisioning,
    provisioningTimedOut,
    provisioningError,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/projects", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const onSignIn = () => {
    void signIn();
  };
  const onSignOut = () => {
    void signOut();
  };

  // ---- Provisioning + timed-out are orthogonal to the splash redesign:
  // they keep their compact card so the user has somewhere to click out of.
  if (provisioning && provisioningTimedOut) {
    // When the backend returned a structured 422 (malformed Cognito
    // attributes), render the exact ``detail`` + ``remediation`` text
    // it computed. Falls back to the generic "couldn't set up" copy
    // for the 15-second timeout case (no operator-actionable detail).
    return (
      <main style={cardStyles.viewport}>
        <div style={cardStyles.card}>
          <div style={cardStyles.brand}>
            <h1 style={cardStyles.brandTitle}>SunWave Spark</h1>
          </div>
          {provisioningError ? (
            <div
              role="alert"
              style={cardStyles.errorBox}
              data-testid="provisioning-error"
            >
              <div
                style={{ fontWeight: 600, marginBottom: 6 }}
                data-testid="provisioning-error-detail"
              >
                {provisioningError.detail}
              </div>
              <div
                style={{ fontSize: "0.875rem" }}
                data-testid="provisioning-error-remediation"
              >
                {provisioningError.remediation}
              </div>
            </div>
          ) : (
            <div
              role="alert"
              style={cardStyles.errorBox}
              data-testid="provisioning-timed-out"
            >
              We couldn't set up your account. Try signing out and signing
              back in.
            </div>
          )}
          <button
            type="button"
            onClick={onSignOut}
            style={cardStyles.button}
            data-testid="provisioning-signout-button"
          >
            Sign out
          </button>
        </div>
      </main>
    );
  }
  if (provisioning) {
    return (
      <main style={cardStyles.viewport}>
        <div style={cardStyles.card}>
          <div style={cardStyles.brand}>
            <h1 style={cardStyles.brandTitle}>SunWave Spark</h1>
          </div>
          <div
            role="status"
            style={cardStyles.status}
            data-testid="provisioning-status"
          >
            Setting up your account…
          </div>
          <button
            type="button"
            onClick={onSignOut}
            style={cardStyles.linkButton}
            data-testid="provisioning-signout-link"
          >
            Sign out
          </button>
        </div>
      </main>
    );
  }

  // ---- Default unauthenticated splash: hex.tech-style hero + 3 pillars.
  return (
    <main style={splashStyles.viewport}>
      <div style={splashStyles.content}>
        <section style={splashStyles.hero}>
          <SunwaveLogo height={72} style={{ marginBottom: 28 }} />
          <h1 style={splashStyles.headline} data-testid="splash-headline">
            Solar layout, finally understood.
          </h1>
          <p style={splashStyles.subhead}>
            Spark turns engineering deliverables into CAD-ready solar layouts.
            Drop in your KMZ boundaries and datasheets — Spark extracts,
            packs, and emits DWG.
          </p>
          <button
            type="button"
            onClick={onSignIn}
            disabled={isLoading}
            style={isLoading ? splashStyles.startDisabled : splashStyles.start}
            data-testid="sign-in-button"
          >
            {isLoading ? "Loading…" : "Start"}
          </button>
          <p style={splashStyles.cta_footnote}>
            Sign-in handled by your organization's identity provider.
          </p>
        </section>

        <section
          style={splashStyles.pillars}
          aria-label="What Spark does"
          data-testid="splash-pillars"
        >
          <PillarCard
            label="01"
            title="Extract"
            description="Parse DWG, PDF, KML, and Excel server-side. Files become structured, queryable data."
            testId="pillar-extract"
          />
          <PillarCard
            label="02"
            title="Understand"
            description="Domain-aware. The system knows what a tracker is, what a setback is, what a rule looks like."
            testId="pillar-understand"
          />
          <PillarCard
            label="03"
            title="Generate"
            description="Constraint-aware packing emits CAD-ready DWG. Engineers refine in AutoCAD."
            testId="pillar-generate"
          />
        </section>
      </div>

      <footer style={splashStyles.footer}>
        <span>© 2026 Sunwave Digital</span>
      </footer>
    </main>
  );
}

function PillarCard({
  label,
  title,
  description,
  testId,
}: {
  label: string;
  title: string;
  description: string;
  testId: string;
}) {
  return (
    <article style={splashStyles.pillar} data-testid={testId}>
      <div style={splashStyles.pillarLabel}>{label}</div>
      <h2 style={splashStyles.pillarTitle}>{title}</h2>
      <p style={splashStyles.pillarBody}>{description}</p>
    </article>
  );
}

/* ----------------------------------------------------------------- */
/* Splash visual tokens (hex.tech-inspired, Stripe-Dashboard adjacent) */
/* ----------------------------------------------------------------- */

const ACCENT = "#635bff";
const ACCENT_HOVER = "#574fdc";

const splashStyles: Record<string, React.CSSProperties> = {
  viewport: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)",
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flex: 1,
    width: "100%",
    maxWidth: 1080,
    margin: "0 auto",
    padding: "120px 24px 80px",
  },
  hero: {
    maxWidth: 760,
    marginBottom: 80,
  },
  headline: {
    fontSize: "clamp(2.25rem, 5vw, 3.5rem)",
    fontWeight: 700,
    letterSpacing: "-0.025em",
    lineHeight: 1.05,
    color: "#0a0e27",
    margin: 0,
    hyphens: "none",
  },
  subhead: {
    fontSize: "clamp(1rem, 1.4vw, 1.25rem)",
    fontWeight: 400,
    color: "#475569",
    lineHeight: 1.55,
    maxWidth: 640,
    margin: "20px 0 36px",
  },
  start: {
    background: ACCENT,
    color: "#ffffff",
    border: "none",
    padding: "12px 40px",
    borderRadius: 8,
    fontSize: "1rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 120ms ease",
  },
  startDisabled: {
    background: ACCENT_HOVER,
    color: "#ffffff",
    border: "none",
    padding: "12px 40px",
    borderRadius: 8,
    fontSize: "1rem",
    fontWeight: 500,
    opacity: 0.7,
    cursor: "not-allowed",
  },
  cta_footnote: {
    fontSize: "0.8125rem",
    color: "#8898aa",
    margin: "16px 0 0",
  },
  pillars: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 24,
    marginTop: 24,
  },
  pillar: {
    background: "#ffffff",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    borderRadius: 12,
    padding: 32,
    boxShadow:
      "0 0 0 1px rgba(50, 50, 93, 0.02), 0 4px 8px -3px rgba(50, 50, 93, 0.06)",
  },
  pillarLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#8898aa",
    letterSpacing: "0.06em",
    marginBottom: 8,
  },
  pillarTitle: {
    fontSize: "1.4rem",
    fontWeight: 700,
    letterSpacing: "-0.015em",
    color: "#0a0e27",
    margin: "0 0 10px",
  },
  pillarBody: {
    fontSize: "0.9375rem",
    color: "#475569",
    lineHeight: 1.55,
    margin: 0,
  },
  footer: {
    width: "100%",
    maxWidth: 1080,
    margin: "0 auto",
    padding: "32px 24px",
    fontSize: "0.8125rem",
    color: "#8898aa",
  },
};

/* ----- compact card styles used by provisioning + timed-out branches ----- */

const cardStyles: Record<string, React.CSSProperties> = {
  viewport: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem 1rem",
    background: "#fafafa",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "#ffffff",
    borderRadius: 12,
    padding: "2.5rem 2rem 2rem",
    boxShadow:
      "0 0 0 1px rgba(50, 50, 93, 0.04), 0 6px 12px -3px rgba(50, 50, 93, 0.08)",
  },
  brand: {
    marginBottom: "1.25rem",
    textAlign: "center",
  },
  brandTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    letterSpacing: "-0.02em",
    color: "#0a0e27",
    margin: 0,
  },
  button: {
    width: "100%",
    background: ACCENT,
    color: "#ffffff",
    border: "none",
    padding: "0.75rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontWeight: 500,
    cursor: "pointer",
  },
  linkButton: {
    display: "block",
    margin: "0.75rem auto 0",
    background: "transparent",
    color: ACCENT,
    border: "none",
    padding: "0.25rem 0.5rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
  },
  status: {
    width: "100%",
    background: "#f6f9fc",
    color: "#525f7f",
    border: "1px solid #e3e8ee",
    padding: "0.75rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    textAlign: "center",
  },
  errorBox: {
    width: "100%",
    background: "#fff7f5",
    color: "#9a3412",
    border: "1px solid #fed7aa",
    padding: "0.75rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    textAlign: "center",
    marginBottom: "0.75rem",
    lineHeight: 1.5,
  },
};
